import { useEffect } from 'react'

import PropTypes from 'prop-types'

import { FetchHelper } from '../../helpers/fetch'

import { useAppContext } from '../useAppContext'
import { useSimpleState } from '../useSimpleState'

import dayjs from 'dayjs'

const useControlSearch = (props={}) => {
    const {ac} = useAppContext()

    const {_state} = useSimpleState({})

    const {_state: _params} = useSimpleState(props.params || '')

    const {_state: _page} = useSimpleState(props.page || 1)

    const {_state: _search} = useSimpleState(props.search || '')

    const {_state: _sort} = useSimpleState(props.sort || { sortBy: 'date', sortDir: 'desc' })

    const {_state: _results} = useSimpleState()

    const fetch = () => {
        if (props.beforeFetch)
            props.beforeFetch()

        const queryParts = []

        if (_params.get() !== '')
            queryParts.push(_params.get())

        if (_search.get() !== '')
            queryParts.push(`q=${encodeURIComponent(_search.get())}`)

        queryParts.push(`sortBy=${_sort.get('sortBy')}`)
        queryParts.push(`sortDir=${_sort.get('sortDir')}`)
        queryParts.push(`page=${_page.get()}`)

        return FetchHelper({
            url: `/api/v1/deals-admin?${queryParts.join('&')}`
        })
            .then((res) => {
                if (res.status === 200) {
                    _results.set(res)
                }
            })
            .catch((res) => {
                console.log('control search fetch error', res)
            })
            .finally(() => {
                if (props.afterFetch)
                    props.afterFetch()
            })
    }

    const fetchGetDeal = (id) => {
        if (props.beforeFetch)
            props.beforeFetch()

        // the admin-scoped endpoint, not the public /deals/{id} one — that
        // one is DealResource for the public PDP page and deliberately
        // omits publish window, archive/delete state, and click counters,
        // none of which the edit page can do without
        return FetchHelper({
            url: `/api/v1/deals-admin/${id}`
        })
            .then((res) => {
                return res
            })
            .catch((res) => {
                return res
            })
            .finally(() => {
                if (props.afterFetch)
                    props.afterFetch()
            })
    }
    
    const fetchCreate = (body) => {
        if (props.beforeFetch)
            props.beforeFetch()

        return FetchHelper({
            url: '/api/v1/deals/post',
            options: {
                method: 'post',
                data: body
            }
        })
            .then((res) => {
                return res
            })
            .catch((res) => {
                return res
            })
            .finally(() => {
                if (props.afterFetch)
                    props.afterFetch()
            })
    }

    const fetchEdit = (body) => {
        if (props.beforeFetch)
            props.beforeFetch()

        return FetchHelper({
            url: '/api/v1/deals/update',
            options: {
                method: 'put',
                data: body
            }
        })
            .then((res) => {
                return res
            })
            .catch((res) => {
                return res
            })
            .finally(() => {
                if (props.afterFetch)
                    props.afterFetch()
            })
    }

    const fetchPublish = (item, status) => {
        if (props.beforeFetch)
            props.beforeFetch()

        return FetchHelper({
            url: '/api/v1/deals/publish',
            options: {
                method: 'put',
                data: {
                    id: item.id,
                    publish: status
                }
            }
        })
            .then((res) => {
                if (res.status === 200) {
                    fetch()
                }

                return res
            })
            .catch((res) => {
                return res
            })
            .finally(() => {
                if (props.afterFetch)
                    props.afterFetch()
            })
    }

    const fetchArchive = (item, status) => {
        if (props.beforeFetch)
            props.beforeFetch()

        return FetchHelper({
            url: '/api/v1/deals/archive',
            options: {
                method: 'put',
                data: {
                    id: item.id,
                    archive: status
                }
            }
        })
            .then((res) => {
                if (res.status === 200) {
                    fetch()
                }

                return res
            })
            .catch((res) => {
                return res
            })
            .finally(() => {
                if (props.afterFetch)
                    props.afterFetch()
            })
    }

    const fetchDead = (item, status) => {
        if (props.beforeFetch)
            props.beforeFetch()

        return FetchHelper({
            url: '/api/v1/deals/dead',
            options: {
                method: 'put',
                data: {
                    id: item.id,
                    dead: status
                }
            }
        })
            .then((res) => {
                if (res.status === 200) {
                    fetch()
                }

                return res
            })
            .catch((res) => {
                return res
            })
            .finally(() => {
                if (props.afterFetch)
                    props.afterFetch()
            })
    }

    const fetchDelete = (item, status) => {
        if (props.beforeFetch)
            props.beforeFetch()

        return FetchHelper({
            url: '/api/v1/deals/delete',
            options: {
                method: 'put',
                data: {
                    id: item.id,
                    delete: status
                }
            }
        })
            .then((res) => {
                if (res.status === 200) {
                    fetch()
                }
                
                return res
            })
            .catch((res) => {
                return res
            })
            .finally(() => {
                if (props.afterFetch)
                    props.afterFetch()
            })
    }

    const fetchResetClicksDead = (item) => {
        if (props.beforeFetch)
            props.beforeFetch()

        return FetchHelper({
            url: '/api/v1/deals/reset-clicks-dead',
            options: {
                method: 'put',
                data: {
                    id: item.id
                }
            }
        })
            .then((res) => {
                if (res.status === 200) {
                    fetch()
                }

                return res
            })
            .catch((res) => {
                return res
            })
            .finally(() => {
                if (props.afterFetch)
                    props.afterFetch()
            })
    }

    const goToPage = (page) => {
        _page.set(page)
    }

    const setSort = (sortBy) => {
        const currentSortBy = _sort.get('sortBy')
        const currentSortDir = _sort.get('sortDir')

        if (currentSortBy === sortBy) {
            _sort.set('sortDir', currentSortDir === 'asc' ? 'desc' : 'asc')
        } else {
            _sort.set('sortBy', sortBy)
            _sort.set('sortDir', 'desc')
        }

        _page.set(1)
    }

    useEffect(() => {
        fetch()
    }, [_params.get(), _page.get(), _search.get(), _sort.get('sortBy'), _sort.get('sortDir')])

    return Object.freeze({
        _results,
        _params,
        _page,
        _search,
        _sort,
        _control: {
            fetch,
            fetchGetDeal,
            fetchEdit,
            fetchCreate,
            fetchPublish,
            fetchArchive,
            fetchDead,
            fetchDelete,
            fetchResetClicksDead,
            goToPage,
            setSort,
        }
    })
}

useControlSearch.propTypes = {
    params: PropTypes.string,
    page: PropTypes.number,
    search: PropTypes.string,
    sort: PropTypes.shape({
        sortBy: PropTypes.string,
        sortDir: PropTypes.string
    }),
    beforeFetch: PropTypes.func,
    afterFetch: PropTypes.func,
}

export { useControlSearch }